/**
 * Product Registration — Wufoo embed (E20-1457)
 *
 * A stopgap so warranty registration keeps working through cutover; E20-1452 replaces
 * the embed with a native form and deletes this file.
 *
 * Why Wufoo's own loader rather than a plain <iframe>: the script runs a postMessage
 * resize handshake with the framed form, so it sizes itself to its content at every
 * width. A cross-origin iframe cannot measure itself, which is why the SFCC lookup
 * carries hardcoded per-breakpoint heights instead — see
 * docs/dev/architecture/order-status-modal.md.
 *
 * Config arrives on data attributes so the section stays the single source of truth.
 *
 * The loading overlay, its timeout and the aria-busy bookkeeping come from util.embed-loading,
 * shared with the SFCC order lookup (E20-1658). What differs here is detection: Wufoo injects
 * the iframe itself, so this cannot attach `load` up front the way the lookup does — it starts
 * watching the mount BEFORE calling display(), then listens to whatever lands.
 */
import { startEmbedLoading, awaitInjectedFrame, frameHasLoaded } from 'util.embed-loading';

if (!customElements.get('bfx-product-registration')) {
  const WUFOO_SCRIPT = 'https://secure.wufoo.com/scripts/embed/form.js';

  /**
   * One shared, memoised load per page — the tag is created once and every caller gets
   * the same settled outcome.
   *
   * Deliberately not "look for an existing <script> and listen to it": a node that has
   * already fired load or error never fires again, so an instance connecting afterwards
   * (a theme-editor re-render, or a second section on the page) would attach listeners
   * for events that are already in the past and wait forever. That left the fallback
   * message hidden and the reserved height in place — a blank box with no explanation.
   * Caching the promise means a failed load rejects immediately for everyone after it.
   */
  let wufooLoad = null;

  const loadWufoo = () => {
    if (window.WufooForm) return Promise.resolve();
    if (wufooLoad) return wufooLoad;

    wufooLoad = new Promise((resolve, reject) => {
      // One controller for both listeners, aborted the moment either fires. `once` alone
      // would only clear the listener that won, leaving the other attached to a script
      // node that lives for the rest of the page.
      const listeners = new AbortController();
      const { signal } = listeners;

      const loader = document.createElement('script');
      loader.src = WUFOO_SCRIPT;
      loader.async = true;

      loader.addEventListener(
        'load',
        () => {
          listeners.abort();
          resolve();
        },
        { signal }
      );

      loader.addEventListener(
        'error',
        () => {
          listeners.abort();
          reject(new Error('Wufoo embed script failed to load'));
        },
        { signal }
      );

      document.head.appendChild(loader);
    });

    return wufooLoad;
  };

  class BfxProductRegistration extends HTMLElement {
    connectedCallback() {
      this.mount = this.querySelector('[data-wufoo-mount]');
      this.fallbackMessage = this.querySelector('[data-wufoo-fallback-message]');
      if (!this.mount) return;

      // A reconnect after rendering has to re-arm the label watcher itself.
      // disconnectedCallback tears the observer down, and renderForm() exits at
      // hasRendered, so without this an element moved in the DOM between display()
      // and the iframe actually landing would leave the frame permanently unnamed.
      // labelFrame() is safe to call again: it re-applies the attribute if the frame
      // is already there, and re-observes only if it isn't.
      if (this.hasRendered) {
        this.labelFrame();
        return;
      }

      // One controller for the injected frame's `load` listener, which is the only listener
      // this element owns directly. The two belonging to the shared Wufoo loader
      // are created once per page and tear themselves down through the loader's own
      // controller as soon as either fires. The observers — labelFrame's and the frame
      // watcher's — disconnect themselves on success and again in disconnectedCallback.
      // What still needs guarding is this element's state by the time the shared promise
      // settles, which the checks in renderForm() below cover.
      this.abortController = new AbortController();
      // Started before the vendor script is even fetched, so the reserved box shows a loading
      // state from the moment this element upgrades rather than sitting empty. On timeout the
      // page's own fallback is revealed instead of a second message — see revealFallback().
      this.loading = startEmbedLoading(this, { onTimeout: () => this.timeOutToFallback() });

      loadWufoo()
        .then(() => this.renderForm())
        .catch((error) => this.handleFailure(error));
    }

    renderForm() {
      // Writing a form into a detached mount does nothing useful and leaves the vendor
      // holding a stale node.
      if (!this.isConnected) return;

      // Once per element, not once per connect. connectedCallback fires on every
      // reconnect and each one adds another `.then()` to the shared promise, so without
      // this a disconnect/reconnect while loading calls display() twice — and a
      // reconnect after a successful load rebuilds the iframe, wiping whatever the
      // customer had already typed. A genuinely re-rendered section arrives as a new
      // element, which gets its own flag and renders normally.
      if (this.hasRendered) return;
      this.hasRendered = true;

      const { wufooUser, wufooHash, wufooHeader } = this.dataset;

      // display() writes the iframe into the element whose id matches the form hash,
      // replacing whatever is inside it. Nothing of ours lives there for exactly that reason:
      // the fallback sits outside the mount, and CSS keeps it hidden until this element says
      // otherwise, so a frame that is injected and then hangs still has a way out.
      const form = new window.WufooForm();
      form.initialize({
        userName: wufooUser,
        formHash: wufooHash,
        autoResize: true,
        async: true,
        host: 'wufoo.com',
        header: wufooHeader,
        ssl: true,
      });
      // Watching starts before display() writes the frame, for the same reason the SFCC lookup
      // attaches its listener before promoting `src`: a frame that loads before anyone is
      // listening never announces it, and the timeout would then bury a working form.
      this.frameWait = awaitInjectedFrame(this.mount);
      this.frameWait.frame
        .then((frame) => frameHasLoaded(frame, this.abortController?.signal))
        .then(() => this.loading?.settle());

      form.display();
      this.labelFrame();
    }

    /**
     * Wufoo builds the iframe itself and gives it no accessible name, so a screen reader
     * announces an unlabelled frame — the same gap E20-1451 closed on the SFCC lookup,
     * which sets `title` directly in Liquid because the theme owns that markup. Here the
     * vendor owns it and `initialize()` has no option for a title, so the attribute goes
     * on after the fact.
     *
     * display() writes the node synchronously today, so the first attempt normally wins.
     * The observer covers `async: true` deferring it, and is disconnected the moment the
     * frame is labelled so it never outlives the one mutation it is waiting for.
     */
    labelFrame() {
      const title = this.dataset.wufooTitle;
      if (!title) return;

      const apply = () => {
        const frame = this.mount.querySelector('iframe');
        if (!frame) return false;
        frame.setAttribute('title', title);
        return true;
      };

      if (apply()) return;

      this.frameWatcher = new MutationObserver(() => {
        if (apply()) this.stopWatchingForFrame();
      });
      this.frameWatcher.observe(this.mount, { childList: true, subtree: true });
    }

    stopWatchingForFrame() {
      this.frameWatcher?.disconnect();
      this.frameWatcher = null;
    }

    disconnectedCallback() {
      this.stopWatchingForFrame();
      this.frameWait?.cancel();
      this.abortController?.abort();

      // Which of these two is correct depends on whether a reconnect can still finish the load.
      //
      // Not yet rendered: destroy(). The timer stops without touching classes, so an element
      // moved in the DOM doesn't flash a fallback on its way back in, and connectedCallback
      // runs in full on the way back — new controller, new loading state, render proceeds.
      //
      // Already rendered: settle(). The aborts above have just killed the frame's `load`
      // listener, and connectedCallback returns early at `hasRendered`, so nothing will ever
      // call settle() again. Leaving destroy() here strands the element in `.is-loading` with
      // aria-busy='true' permanently — assistive tech reporting a region that never finishes
      // loading, over a form that is sitting there working. Dropping the overlay is the lesser
      // wrong: worst case it uncovers a frame that is still painting, for a moment.
      if (this.hasRendered) {
        this.loading?.settle();
      } else {
        this.loading?.destroy();
      }
    }

    /**
     * Wufoo blocked, offline, or throwing. The mount still holds the fallback link to
     * the hosted form, so the customer can still register — just not inline. Reveal the
     * explanation and say so out loud rather than leaving a blank page.
     *
     * Unhiding the message is what announces it: it carries role='alert', so removing
     * `hidden` puts it into the accessibility tree and screen readers read it out. That
     * matters here because the failure can land seconds after the page settled, long
     * after a screen-reader user has read past this point.
     */
    handleFailure(error) {
      console.error('[bfx-product-registration] Wufoo embed unavailable:', error);
      if (!this.isConnected) return;

      this.revealFallback();
    }

    /**
     * The embed never answered within the shared timeout. Same outcome as an outright failure
     * from the customer's point of view, so it takes the same path.
     */
    timeOutToFallback() {
      console.warn('[bfx-product-registration] Wufoo embed timed out; showing the fallback.');
      if (!this.isConnected) return;

      this.revealFallback();
    }

    /**
     * Show the hosted-form link and say why. Drops the loading overlay as it goes — the
     * fallback lives inside the mount, so an overlay left covering the box would hide the very
     * thing being revealed. This is also why the section renders no `failed_text`: the fallback
     * IS the failure message here.
     */
    revealFallback() {
      this.classList.add('is-embed-unavailable');
      this.fallbackMessage?.removeAttribute('hidden');
      this.loading?.settle();
    }
  }

  customElements.define('bfx-product-registration', BfxProductRegistration);
}
